import i18n from 'src/i18n'
import { cleanRut } from '../../../utils/formatter';
import {
  isFilterAmountLike,
  looksLikeDecimalFilterAmount,
  parseFilterAmount,
} from '../../../utils/amountInput';
import { getTransactionsFilter } from '../../../redux/actions/conciliacion';
import { importDocumentsFilter } from '../../../redux/actions/documentostributarios';
import { importAsientosSearchFilter } from '../../../redux/actions/asientos';
import { importEnhancedIcons } from '../../../redux/actions/enhancedIcons';

const emptyResult = (keys) =>
  keys.reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});

/**
 * Round number of 8-9 digits (e.g. 150000000, 150.000.000) typed without RUT markers (dash / K).
 * Only this length is ambiguous with a RUT; a RUT body is rarely a multiple of 1000, so it is an amount.
 */
export function isRoundAmountTerm(term) {
  const t = String(term ?? '').trim().replace(/^\$\s*/, '');
  if (!/^\d[\d.\s]*$/.test(t)) return false;
  const digits = t.replace(/[^\d]/g, '');
  return /^\d{8,9}$/.test(digits) && /000$/.test(digits);
}

/**
 * RUT = 7-8 digit body + verifier. With an explicit marker (dash or K) it is
 * always a RUT; plain digits are a RUT unless they are a round amount
 * (so 150000000 is an amount, not a RUT).
 */
function isRutLike(trimmed) {
  const rutClean = cleanRut(trimmed).toUpperCase();
  if (!/^[0-9]{7,8}[0-9K]$/.test(rutClean)) return false;
  if (/[-kK]/.test(trimmed)) return true;
  return !isRoundAmountTerm(trimmed);
}

function isNumericOnly(trimmed) {
  const digits = trimmed.replace(/[^0-9]/g, '');
  return digits.length > 0 && trimmed.replace(/[\s$.,;]/g, '') === digits;
}

export function isChileanThousandsTerm(term) {
  return /^\d{1,3}(\.\d{3})+$/.test(String(term ?? '').trim());
}

/** Ledger code (1.01, 1.01.02) — excludes 20.000-style thousands. */
export function isAccountCodeTerm(term) {
  const t = String(term ?? '').trim();
  if (!/^\d+(\.\d+)+$/.test(t)) return false;
  return !isChileanThousandsTerm(t);
}

export const TRANSACTIONS_SEARCH_KEYS = ['minAmount', 'maxAmount', 'rut', 'description'];

export const DOCUMENTS_SEARCH_KEYS = ['amountStart', 'amountEnd', 'rut', 'name', 'docNum'];

export const ASIENTOS_SEARCH_KEYS = [
  'entryNumber',
  'minAmount',
  'maxAmount',
  'accountQuery',
  'description',
  'textSearch',
];

export function parseTransactionsSearchFilter(name, value) {
  if (['amount', 'minAmount', 'maxAmount'].includes(name)) {
    return parseFilterAmount(value);
  }
  if (['rut', 'description'].includes(name)) {
    return value && String(value).trim() !== '' ? String(value).trim() : null;
  }
  return value;
}

export function parseDocumentsSearchFilter(name, value) {
  if (['amountStart', 'amountEnd'].includes(name)) {
    return parseFilterAmount(value);
  }
  if (name === 'rut') {
    const cleaned = String(value ?? '').replace(/[^0-9Kk]/g, '');
    return cleaned || null;
  }
  if (['name', 'docNum'].includes(name)) {
    return value && String(value).trim() !== '' ? String(value).trim() : null;
  }
  return value;
}

export function inferTransactionsSearchFilters(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return emptyResult(TRANSACTIONS_SEARCH_KEYS);

  if (isRutLike(trimmed)) {
    return { rut: cleanRut(trimmed), minAmount: null, maxAmount: null, description: null };
  }
  if (isFilterAmountLike(trimmed) || isNumericOnly(trimmed)) {
    const n = parseFilterAmount(trimmed);
    if (n == null) {
      return { description: trimmed, minAmount: null, maxAmount: null, rut: null };
    }
    return { minAmount: n, maxAmount: n, rut: null, description: null };
  }
  return { description: trimmed, minAmount: null, maxAmount: null, rut: null };
}

function isFolioLike(trimmed) {
  if (!/\d/.test(trimmed)) return false;
  return /^[\d\s;]+$/.test(trimmed);
}

/** Documents: decimals and round 8-9 digit numbers are amounts; other integers stay folios. */
function isDocumentAmountTerm(raw) {
  return looksLikeDecimalFilterAmount(raw) || isRoundAmountTerm(raw);
}

export function inferDocumentsSearchFilters(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return emptyResult(DOCUMENTS_SEARCH_KEYS);

  if (isRutLike(trimmed)) {
    return {
      rut: cleanRut(trimmed),
      name: null,
      docNum: null,
      amountStart: null,
      amountEnd: null,
    };
  }
  if (isDocumentAmountTerm(trimmed)) {
    const n = parseFilterAmount(trimmed);
    return {
      amountStart: n,
      amountEnd: n,
      rut: null,
      name: null,
      docNum: null,
    };
  }
  if (isFolioLike(trimmed) || isNumericOnly(trimmed)) {
    return {
      docNum: trimmed.replace(/[^\d;]/g, '').replace(/;+/g, ';'),
      rut: null,
      name: null,
      amountStart: null,
      amountEnd: null,
    };
  }
  return {
    name: trimmed,
    rut: null,
    docNum: null,
    amountStart: null,
    amountEnd: null,
  };
}

export function parseAsientosSearchFilter(name, value) {
  if (name === 'entryNumber') {
    const digits = String(value ?? '').replace(/[^\d]/g, '');
    if (!digits) return null;
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? n : null;
  }
  if (['minAmount', 'maxAmount'].includes(name)) {
    return parseFilterAmount(value);
  }
  if (['accountQuery', 'description', 'textSearch'].includes(name)) {
    return value && String(value).trim() !== '' ? String(value).trim() : null;
  }
  return value;
}

export function inferAsientosSearchFilters(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return emptyResult(ASIENTOS_SEARCH_KEYS);

  const hash = trimmed.match(/^#\s*(\d+)$/);
  if (hash) {
    return {
      entryNumber: Number(hash[1]),
      minAmount: null,
      maxAmount: null,
      accountQuery: null,
      description: null,
      textSearch: null,
    };
  }

  if (trimmed.startsWith('$')) {
    const n = parseFilterAmount(trimmed);
    return {
      entryNumber: null,
      minAmount: n,
      maxAmount: n,
      accountQuery: null,
      description: null,
      textSearch: null,
    };
  }

  if (isAccountCodeTerm(trimmed)) {
    return {
      entryNumber: null,
      minAmount: null,
      maxAmount: null,
      accountQuery: trimmed,
      description: null,
      textSearch: null,
    };
  }

  if (isChileanThousandsTerm(trimmed) || looksLikeDecimalFilterAmount(trimmed)) {
    const n = parseFilterAmount(trimmed);
    return {
      entryNumber: null,
      minAmount: n,
      maxAmount: n,
      accountQuery: null,
      description: null,
      textSearch: null,
    };
  }

  if (/^\d+$/.test(trimmed)) {
    return {
      entryNumber: Number(trimmed),
      minAmount: null,
      maxAmount: null,
      accountQuery: null,
      description: null,
      textSearch: null,
    };
  }

  if (isFilterAmountLike(trimmed)) {
    const n = parseFilterAmount(trimmed);
    return {
      entryNumber: null,
      minAmount: n,
      maxAmount: n,
      accountQuery: null,
      description: null,
      textSearch: null,
    };
  }

  return {
    entryNumber: null,
    minAmount: null,
    maxAmount: null,
    accountQuery: null,
    description: null,
    textSearch: trimmed,
  };
}

export function hasModuleSearchFilter(variables, keys) {
  return keys.some((key) => {
    const value = variables?.[key];
    return value != null && value !== '';
  });
}

export function stripModuleSearchFields(variables, keys) {
  const next = { ...variables };
  keys.forEach((key) => delete next[key]);
  delete next.name;
  delete next.folio;
  return next;
}

function shouldAddToUrl(parseValue) {
  const isNumericValue = typeof parseValue === 'number';
  const isTruthyString = typeof parseValue === 'string' && parseValue !== '';
  return (
    parseValue != null &&
    parseValue !== '' &&
    (isNumericValue || isTruthyString || parseValue === true)
  );
}

export function syncModuleSearchFieldsToUrl(searchParams, fields, keys, options = {}) {
  keys.forEach((key) => {
    const parseValue = fields[key];
    if (shouldAddToUrl(parseValue)) {
      searchParams.set(key, parseValue.toString());
    } else {
      searchParams.delete(key);
    }
  });
  (options.clearParams || []).forEach((key) => searchParams.delete(key));
  const pageParam = options.pageParam ?? 'page';
  if (pageParam === 'offset') {
    searchParams.delete('offset');
  } else {
    searchParams.set('page', '1');
  }
}

export function buildVariablesFromPanelInput(variables, searchInputValues, keys, parseFilters) {
  const newVariables = { ...variables };
  keys.forEach((filterName) => {
    const inputValue = searchInputValues[filterName];
    if (inputValue != null && String(inputValue).trim() !== '') {
      newVariables[filterName] = parseFilters(filterName, inputValue);
    } else {
      delete newVariables[filterName];
    }
  });
  return newVariables;
}

export function mergeInferredIntoVariables(variables, inferred, keys) {
  const next = stripModuleSearchFields(variables, keys);
  keys.forEach((key) => {
    if (inferred[key] != null && inferred[key] !== '') {
      next[key] = inferred[key];
    }
  });
  return next;
}

export function transactionsPanelValuesFromVariables(variables) {
  return {
    minAmount: variables.minAmount != null ? String(variables.minAmount) : '',
    maxAmount: variables.maxAmount != null ? String(variables.maxAmount) : '',
    rut: variables.rut || '',
    description: variables.description || '',
  };
}

export function documentsPanelValuesFromVariables(variables) {
  return {
    amountStart: variables.amountStart != null ? String(variables.amountStart) : '',
    amountEnd: variables.amountEnd != null ? String(variables.amountEnd) : '',
    rut: variables.rut || '',
    name: variables.name || '',
    docNum: variables.docNum || '',
  };
}

export function transactionsMainQueryFromVariables(variables) {
  if (variables.rut) return variables.rut;
  if (
    variables.minAmount != null &&
    variables.maxAmount != null &&
    variables.minAmount === variables.maxAmount
  ) {
    return String(variables.minAmount);
  }
  if (variables.description) return variables.description;
  return '';
}

export function documentsMainQueryFromVariables(variables) {
  if (variables.rut) return variables.rut;
  if (variables.docNum) return String(variables.docNum);
  if (variables.name) return variables.name;
  return '';
}

export function asientosPanelValuesFromVariables(variables = {}) {
  return {
    entryNumber: variables.entryNumber != null ? String(variables.entryNumber) : '',
    minAmount: variables.minAmount != null ? String(variables.minAmount) : '',
    maxAmount: variables.maxAmount != null ? String(variables.maxAmount) : '',
    accountQuery: variables.accountQuery || '',
    description: variables.description || '',
  };
}

export function asientosSearchFieldsFromUrl(searchParams) {
  const fields = emptyResult(ASIENTOS_SEARCH_KEYS);
  const entryNumber = searchParams.get('entryNumber');
  if (entryNumber) {
    const n = parseInt(entryNumber, 10);
    if (Number.isFinite(n)) fields.entryNumber = n;
  }
  const minAmount = searchParams.get('minAmount');
  if (minAmount != null && minAmount !== '') {
    const n = parseFilterAmount(minAmount);
    if (n != null) fields.minAmount = n;
  }
  const maxAmount = searchParams.get('maxAmount');
  if (maxAmount != null && maxAmount !== '') {
    const n = parseFilterAmount(maxAmount);
    if (n != null) fields.maxAmount = n;
  }
  const accountQuery = searchParams.get('accountQuery');
  if (accountQuery) fields.accountQuery = accountQuery;
  const description = searchParams.get('description');
  if (description) fields.description = description;
  const textSearch = searchParams.get('textSearch');
  if (textSearch) fields.textSearch = textSearch;
  return fields;
}

export function asientosMainQueryFromVariables(variables = {}) {
  if (variables.entryNumber != null && variables.entryNumber !== '') {
    return String(variables.entryNumber);
  }
  if (
    variables.minAmount != null &&
    variables.maxAmount != null &&
    variables.minAmount === variables.maxAmount
  ) {
    return String(variables.minAmount);
  }
  if (variables.accountQuery) return variables.accountQuery;
  if (variables.description) return variables.description;
  if (variables.textSearch) return variables.textSearch;
  return '';
}

function updateTransactionsEnhancedIcons(dispatch, variables) {
  const keys = Object.keys(variables).filter((k) => variables[k] != null && variables[k] !== '');
  const onlyType = keys.length === 0 || (keys.length === 1 && variables?.type === 'ALL');
  dispatch(importEnhancedIcons({ conciliacion: { icon: onlyType ? '' : 'filterIcon' } }));
}

function updateDocumentsEnhancedIcons(dispatch, variables) {
  const filtered = Object.fromEntries(
    Object.entries(variables).filter(
      ([, value]) => value !== '' && value != null && value !== undefined,
    ),
  );
  dispatch(
    importEnhancedIcons({
      documentostributarios: { icon: Object.keys(filtered).length === 0 ? '' : 'filterIcon' },
    }),
  );
}

function updateAsientosEnhancedIcons(dispatch, variables) {
  const filtered = Object.fromEntries(
    Object.entries(variables || {}).filter(
      ([, value]) => value !== '' && value != null && value !== undefined,
    ),
  );
  dispatch(
    importEnhancedIcons({
      asientos: { icon: Object.keys(filtered).length === 0 ? '' : 'filterIcon' },
    }),
  );
}

export const MODULE_SEARCH_CONFIGS = {
  transactions: {
    searchKeys: TRANSACTIONS_SEARCH_KEYS,
    emptyPanelValues: () => transactionsPanelValuesFromVariables({}),
    panelValuesFromVariables: transactionsPanelValuesFromVariables,
    mainQueryFromVariables: transactionsMainQueryFromVariables,
    parseFilter: parseTransactionsSearchFilter,
    inferSearchFilters: inferTransactionsSearchFilters,
    selectFilters: (state) => state.conciliacion?.getTransactionsFilter,
    dispatchFilters: getTransactionsFilter,
    updateEnhancedIcons: updateTransactionsEnhancedIcons,
    get ariaLabel() { return i18n.t('finance:moduleSearch.transactionsAria') },
    get placeholder() { return i18n.t('finance:moduleSearch.transactionsPlaceholder') },
    filtersGridClass: 'reconcile-filters-container',
  },
  documents: {
    searchKeys: DOCUMENTS_SEARCH_KEYS,
    emptyPanelValues: () => ({
      amountStart: '',
      amountEnd: '',
      rut: '',
      name: '',
      docNum: '',
    }),
    panelValuesFromVariables: documentsPanelValuesFromVariables,
    mainQueryFromVariables: documentsMainQueryFromVariables,
    parseFilter: parseDocumentsSearchFilter,
    inferSearchFilters: inferDocumentsSearchFilters,
    selectFilters: (state) => state.documentostributarios?.documentsFilter,
    dispatchFilters: importDocumentsFilter,
    updateEnhancedIcons: updateDocumentsEnhancedIcons,
    get ariaLabel() { return i18n.t('finance:moduleSearch.documentsAria') },
    get placeholder() { return i18n.t('finance:moduleSearch.documentsPlaceholder') },
    filtersGridClass: 'document-filters-container',
  },
  asientos: {
    searchKeys: ASIENTOS_SEARCH_KEYS,
    emptyPanelValues: () => asientosPanelValuesFromVariables({}),
    panelValuesFromVariables: asientosPanelValuesFromVariables,
    mainQueryFromVariables: asientosMainQueryFromVariables,
    parseFilter: parseAsientosSearchFilter,
    inferSearchFilters: inferAsientosSearchFilters,
    selectFilters: (state) => state.asientos?.searchFilter,
    dispatchFilters: importAsientosSearchFilter,
    updateEnhancedIcons: updateAsientosEnhancedIcons,
    get ariaLabel() { return i18n.t('finance:moduleSearch.asientosAria') },
    get placeholder() { return i18n.t('finance:moduleSearch.asientosPlaceholder') },
    pageParam: 'offset',
    clearParams: ['entryId'],
  },
};

export function getModuleSearchConfig(module) {
  const config = MODULE_SEARCH_CONFIGS[module];
  if (!config) throw new Error(`ModuleSearchBar: módulo desconocido "${module}"`);
  return config;
}
